// Brand Identity System for Resin Surfaces London
export const brandTheme = {
  // Primary Brand Colors - Yellow & Black Theme
  colors: {
    primary: {
      50: '#fefce8',   // Very light yellow
      100: '#fef3c7',  // Light yellow
      200: '#fde68a',  // Lighter yellow
      300: '#fcd34d',  // Light yellow
      400: '#fbbf24',  // Medium yellow
      500: '#FECE1B',  // Main brand yellow
      600: '#d97706',  // Darker yellow
      700: '#b45309',  // Dark yellow
      800: '#92400e',  // Very dark yellow
      900: '#78350f',  // Darkest yellow
    },
    secondary: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b', // Text secondary
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#000000', // Pure black for strong contrast
    },
    accent: {
      gold: '#FECE1B',    // Main brand yellow
      green: '#10b981',
      red: '#ef4444',
      amber: '#f59e0b',
      yellow: '#FECE1B',  // Brand yellow
      black: '#000000',   // Brand black
    },
    neutral: {
      white: '#ffffff',
      black: '#000000',
      gray: {
        50: '#f9fafb',
        100: '#f3f4f6',
        200: '#e5e7eb',
        300: '#d1d5db',
        400: '#9ca3af',
        500: '#6b7280',
        600: '#4b5563',
        700: '#374151',
        800: '#1f2937',
        900: '#111827',
      }
    }
  },
  
  // Typography System
  typography: {
    fontFamily: {
      primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      heading: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      mono: '"Fira Code", "Monaco", "Consolas", monospace',
    },
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem',    // 48px
      '6xl': '3.75rem', // 60px
      '7xl': '4.5rem',  // 72px
      '8xl': '6rem',    // 96px
      '9xl': '8rem',    // 128px
    },
    fontWeight: {
      thin: '100',
      extralight: '200',
      light: '300',
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      extrabold: '800',
      black: '900',
    },
    lineHeight: {
      none: '1',
      tight: '1.25',
      snug: '1.375',
      normal: '1.5',
      relaxed: '1.625',
      loose: '2',
    }
  },

  // Spacing System
  spacing: {
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    7: '1.75rem',   // 28px
    8: '2rem',      // 32px
    9: '2.25rem',   // 36px
    10: '2.5rem',   // 40px
    12: '3rem',     // 48px
    14: '3.5rem',   // 56px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
    24: '6rem',     // 96px
    28: '7rem',     // 112px
    32: '8rem',     // 128px
    36: '9rem',     // 144px
    40: '10rem',    // 160px
    44: '11rem',    // 176px
    48: '12rem',    // 192px
    52: '13rem',    // 208px
    56: '14rem',    // 224px
    60: '15rem',    // 240px
    64: '16rem',    // 256px
    72: '18rem',    // 288px
    80: '20rem',    // 320px
    96: '24rem',    // 384px
  },

  // Border Radius System
  borderRadius: {
    none: '0',
    sm: '0.125rem',   // 2px
    base: '0.25rem',  // 4px
    md: '0.375rem',   // 6px
    lg: '0.5rem',     // 8px
    xl: '0.75rem',    // 12px
    '2xl': '1rem',    // 16px
    '3xl': '1.5rem',  // 24px
    full: '9999px',
  },

  // Shadow System
  boxShadow: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    none: 'none',
  },

  // Animation System
  animation: {
    duration: {
      fast: '150ms',
      normal: '300ms',
      slow: '500ms',
      slower: '700ms',
    },
    easing: {
      linear: 'linear',
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
    }
  },

  // Breakpoints - Enhanced for better mobile and tablet support
  breakpoints: {
    xs: '320px',    // iPhone SE and smaller
    sm: '390px',    // iPhone 12 and standard mobile
    md: '428px',    // iPhone 12 Pro Max and large mobile
    lg: '768px',    // iPad Air portrait and landscape mobile
    xl: '1024px',   // iPad Air landscape and iPad Pro portrait
    '2xl': '1366px', // iPad Pro landscape and large tablets
    '3xl': '1536px', // Large desktop
  },

  // Z-Index Scale
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  }
};

// Brand-specific component styles
export const brandComponents = {
  button: {
    primary: {
      background: `linear-gradient(135deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.primary[600]} 100%)`,
      color: brandTheme.colors.neutral.black,
      boxShadow: `0 10px 30px ${brandTheme.colors.primary[500]}30`,
      hover: {
        transform: 'translateY(-3px)',
        boxShadow: `0 15px 40px ${brandTheme.colors.primary[500]}40`,
      }
    },
    secondary: {
      background: 'rgba(0, 0, 0, 0.1)',
      color: brandTheme.colors.neutral.white,
      border: '2px solid rgba(254, 206, 27, 0.3)',
      backdropFilter: 'blur(10px)',
      hover: {
        background: 'rgba(254, 206, 27, 0.2)',
        borderColor: 'rgba(254, 206, 27, 0.5)',
      }
    },
    black: {
      background: brandTheme.colors.neutral.black,
      color: brandTheme.colors.primary[500],
      border: `2px solid ${brandTheme.colors.primary[500]}`,
      hover: {
        background: brandTheme.colors.primary[500],
        color: brandTheme.colors.neutral.black,
        transform: 'translateY(-2px)',
      }
    }
  },
  
  card: {
    background: brandTheme.colors.neutral.white,
    borderRadius: brandTheme.borderRadius['2xl'],
    boxShadow: brandTheme.boxShadow.lg,
    border: `1px solid ${brandTheme.colors.neutral.gray[200]}`,
    hover: {
      transform: 'translateY(-5px)',
      boxShadow: brandTheme.boxShadow['2xl'],
    }
  },

  gradient: {
    primary: `linear-gradient(135deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.primary[600]} 100%)`,
    hero: `linear-gradient(135deg, rgba(254, 206, 27, 0.9), rgba(0, 0, 0, 0.8))`,
    text: `linear-gradient(135deg, #FECE1B 0%, #d97706 100%)`,
    yellowBlack: `linear-gradient(135deg, #FECE1B 0%, #000000 100%)`,
    blackYellow: `linear-gradient(135deg, #000000 0%, #FECE1B 100%)`,
  }
};

export default brandTheme;
